import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, ArrowRight } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n, LanguageToggle } from "@/i18n/I18nContext";
import PasswordChecklist, { isPasswordStrong } from "@/components/PasswordChecklist";
import { toast } from "sonner";

const inputCls = "w-full px-4 py-3 rounded-lg bg-white border border-[#E5E2DC] outline-none focus:ring-1 focus:ring-[#2C3E35] focus:border-[#2C3E35] transition";

export default function SetPassword() {
  const { t } = useI18n();
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const valid = isPasswordStrong(form.next) && form.next === form.confirm;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.next !== form.confirm) { setError(t.pw.mismatch); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      await refresh();
      toast.success(t.pw.updated);
      navigate(user?.role === "superadmin" ? "/admin" : "/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFBF9] p-6" data-testid="set-password-page">
      <div className="w-full max-w-md fade-up">
        <div className="flex items-center justify-between mb-8">
          <div className="h-11 w-11 rounded-xl bg-[#2C3E35] flex items-center justify-center"><ShieldCheck size={22} weight="duotone" color="#D4AF37" /></div>
          <LanguageToggle />
        </div>
        <h1 className="font-heading text-3xl font-medium tracking-tight">{t.pw.title}</h1>
        <p className="text-sm text-[#6B6A67] mt-2 mb-7">{t.pw.subtitle}</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label-caps block mb-2">{t.pw.current}</label>
            <input data-testid="current-password" type="password" required value={form.current} onChange={set("current")} className={inputCls} />
          </div>
          <div>
            <label className="label-caps block mb-2">{t.pw.new}</label>
            <input data-testid="new-password" type="password" required value={form.next} onChange={set("next")} className={inputCls} />
            <PasswordChecklist password={form.next} />
          </div>
          <div>
            <label className="label-caps block mb-2">{t.pw.confirm}</label>
            <input data-testid="confirm-password" type="password" required value={form.confirm} onChange={set("confirm")} className={inputCls} />
          </div>
          {error && <div data-testid="set-password-error" className="text-sm text-[#B86B5A] bg-[#B86B5A]/8 rounded-lg px-4 py-2.5">{error}</div>}
          <button data-testid="set-password-submit" type="submit" disabled={loading || !valid}
            className="w-full py-3 rounded-lg bg-[#2C3E35] text-white font-medium hover:bg-[#1E2B25] transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
            {loading ? "…" : <>{t.pw.save} <ArrowRight size={18} /></>}
          </button>
          <button type="button" data-testid="set-password-logout" onClick={() => { logout(); navigate("/login"); }} className="w-full text-sm text-[#6B6A67] hover:text-[#2C3E35] transition-colors">{t.pw.logout}</button>
        </form>
      </div>
    </div>
  );
}
